import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Plus, Pencil, Trash2, Globe } from 'lucide-react';
import { Section } from '@vnetwork/ui/layout';
import {
  Button,
  Input,
  IconButton,
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
  Card,
  CardHeader,
  CardTitle,
  CardBody,
  Skeleton,
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogBody,
  DialogFooter,
} from '@vnetwork/ui/primitives';
import { DataTable, EmptyState, type ColumnDef } from '@vnetwork/ui/data';
import { Field } from '@vnetwork/ui/forms';
import { useToast } from '@vnetwork/ui/feedback';
import { useWaapCorsHeaders, useSetWaapCorsHeaders } from '@/api/waap';
import { errMessage } from '@/pages/services/waap/waap-util';
import styles from './CorsHeaderTab.module.css';

// The CORS response headers the vendor exposes.
const HEADER_KEYS = [
  'Access-Control-Allow-Origin',
  'Access-Control-Expose-Headers',
  'Access-Control-Max-Age',
  'Access-Control-Allow-Credentials',
  'Access-Control-Allow-Methods',
  'Access-Control-Allow-Headers',
];

interface Row {
  key: string;
  value: string;
}

/**
 * CORS Header — website-detail tab. A map of CORS response headers, persisted
 * whole via the private CDN portal. Headers are added/edited in a dialog (not an
 * inline row) so the tab reads as a list + "Add", matching the other tabs; the
 * bottom Save persists the full set. Website fixed by the detail route (`:domainId`).
 */
export function CorsHeaderTab() {
  const { domainId } = useParams<{ domainId: string }>();
  const { show } = useToast();

  const { data, isLoading } = useWaapCorsHeaders(domainId);
  const [rows, setRows] = useState<Row[]>([]);
  useEffect(() => {
    setRows(Object.entries(data ?? {}).map(([key, value]) => ({ key, value })));
  }, [data]);

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingIdx, setEditingIdx] = useState<number | null>(null);
  const [draft, setDraft] = useState<Row>({ key: '', value: '' });

  const update = useSetWaapCorsHeaders(domainId ?? '', {
    onSuccess: () => show({ tone: 'success', title: 'CORS headers saved' }),
    onError: (e) => show({ tone: 'danger', title: 'Save failed', description: errMessage(e) }),
  });

  const openAdd = () => {
    setDraft({ key: '', value: '' });
    setEditingIdx(null);
    setDialogOpen(true);
  };
  const openEdit = (idx: number) => {
    setDraft(rows[idx]);
    setEditingIdx(idx);
    setDialogOpen(true);
  };
  const closeDialog = () => {
    setDialogOpen(false);
    setDraft({ key: '', value: '' });
    setEditingIdx(null);
  };
  const removeRow = (idx: number) => setRows((rs) => rs.filter((_, i) => i !== idx));

  // Keys already chosen elsewhere can't be picked again (the map has unique keys).
  const usedKeys = rows.filter((_, i) => i !== editingIdx).map((r) => r.key);
  const keyOptions = HEADER_KEYS.filter((k) => k === draft.key || !usedKeys.includes(k));
  const draftValid = !!draft.key && draft.value.trim().length > 0;

  const commitDraft = () => {
    if (!draftValid) {
      show({ tone: 'danger', title: 'Header key and value are required' });
      return;
    }
    const next: Row = { key: draft.key, value: draft.value.trim() };
    setRows((rs) => (editingIdx === null ? [...rs, next] : rs.map((r, i) => (i === editingIdx ? next : r))));
    closeDialog();
  };

  const save = () => update.mutate(Object.fromEntries(rows.map((r) => [r.key, r.value])));

  const columns: ColumnDef<Row & { _idx: number }>[] = [
    { accessorKey: 'key', header: 'Header', cell: ({ row }) => <span className={styles.mono}>{row.original.key}</span> },
    { accessorKey: 'value', header: 'Value', cell: ({ row }) => row.original.value },
    {
      id: 'actions',
      header: 'Operation',
      cell: ({ row }) => (
        <div className={styles.rowActions}>
          <IconButton aria-label="Edit header" variant="ghost" onClick={() => openEdit(row.original._idx)}>
            <Pencil size={16} />
          </IconButton>
          <IconButton aria-label="Delete header" variant="ghost" onClick={() => removeRow(row.original._idx)}>
            <Trash2 size={16} />
          </IconButton>
        </div>
      ),
    },
  ];

  if (isLoading) return <Skeleton className={styles.skeleton} />;

  return (
    <Section
      title="CORS Headers"
      description="Configure CORS response headers the edge adds to responses. Saving replaces the full set for this website."
    >
      <Card>
        <CardHeader
          actions={
            <Button
              variant="secondary"
              size="sm"
              startIcon={<Plus size={14} />}
              disabled={rows.length >= HEADER_KEYS.length}
              onClick={openAdd}
            >
              Add CORS Header
            </Button>
          }
        >
          <CardTitle>Response headers</CardTitle>
        </CardHeader>
        <CardBody className={styles.bodyGrid}>
          <DataTable
            columns={columns}
            data={rows.map((r, i) => ({ ...r, _idx: i }))}
            emptyState={
              <EmptyState
                icon={<Globe size={20} />}
                title="No CORS headers yet"
                description="Click “Add CORS Header” to add one."
              />
            }
          />

          <div className={styles.footer}>
            <Button disabled={rows.length === 0 || update.isPending} onClick={save}>
              {update.isPending ? 'Saving…' : 'Save'}
            </Button>
          </div>
        </CardBody>
      </Card>

      <Dialog open={dialogOpen} onOpenChange={(o) => (o ? setDialogOpen(true) : closeDialog())}>
        <DialogContent size="md">
          <DialogHeader>
            <DialogTitle>{editingIdx === null ? 'Add CORS Header' : 'Edit CORS Header'}</DialogTitle>
            <DialogDescription>Pick a CORS response header and set its value. Remember to Save after.</DialogDescription>
          </DialogHeader>
          <DialogBody>
            <div className={styles.dialogForm}>
              <Field label="Header key" required description="Only standard CORS response headers are available (Access-Control-*).">
                {() => (
                  <Select value={draft.key} onValueChange={(v) => setDraft((d) => ({ ...d, key: v }))}>
                    <SelectTrigger>
                      <SelectValue placeholder="Select a header" />
                    </SelectTrigger>
                    <SelectContent>
                      {keyOptions.map((k) => (
                        <SelectItem key={k} value={k}>
                          {k}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
              </Field>
              <Field label="Header value" required>
                {(p) => (
                  <Input
                    {...p}
                    value={draft.value}
                    placeholder="e.g. * or https://example.com"
                    onChange={(e) => setDraft((d) => ({ ...d, value: e.target.value }))}
                  />
                )}
              </Field>
            </div>
          </DialogBody>
          <DialogFooter>
            <Button variant="secondary" onClick={closeDialog}>
              Cancel
            </Button>
            <Button onClick={commitDraft}>{editingIdx === null ? 'Add Header' : 'Update Header'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Section>
  );
}
